import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getNotifications, markAsRead, markAllAsRead, getUnreadCount } from '../utils/api';
import { formatDateTime } from '../utils/dateHelpers';

const NotificationPanel = ({ isOpen, onClose, onOpenTask }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const [notifsRes, countRes] = await Promise.all([
        getNotifications(),
        getUnreadCount()
      ]);
      setNotifications(notifsRes.data);
      setUnreadCount(countRes.data.unread_count);
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId, taskId) => {
    try {
      await markAsRead(notificationId);
      loadNotifications();
      if (taskId) {
        onOpenTask(taskId);
      }
      onClose();
    } catch (error) {

    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      loadNotifications();
    } catch (error) {

    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      <motion.div
        initial={{ x: 400, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 400, opacity: 0 }}
        transition={{ type: 'spring', damping: 25 }}
        className="fixed right-0 top-0 h-full w-full md:w-96 bg-background border-l-2 border-primary/20 z-50 overflow-y-auto shadow-2xl"
      >
        {/* Header con gradiente */}
        <div className="sticky top-0 bg-gradient-to-r from-primary/10 to-info/10 border-b border-border px-6 py-4 z-10 backdrop-blur-md">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-info">
              Notificaciones
            </h3>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>
          </div>
          {unreadCount > 0 && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleMarkAllAsRead}
              className="text-xs text-primary hover:underline font-bold"
            >
              Marcar todas como leídas ({unreadCount})
            </motion.button>
          )}
        </div>
        <div className="p-4 space-y-2">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Cargando...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-5xl mb-3">🔔</div>
              <p className="text-muted-foreground font-semibold">Sin notificaciones</p>
            </div>
          ) : (
            notifications.map((notif, index) => (
              <motion.div
                key={notif.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ x: -4, scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleMarkAsRead(notif.id, notif.task_id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative overflow-hidden ${
                  notif.read
                    ? 'bg-card border-border'
                    : 'bg-gradient-to-r from-primary/5 to-info/5 border-primary/30 shadow-lg'
                }`}
              >
                {!notif.read && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-info"></div>
                )}
                <div className="flex items-start gap-3">
                  {!notif.read && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary mt-1.5 flex-shrink-0 animate-pulse shadow-lg" style={{ boxShadow: '0 0 10px rgba(16,185,129,0.6)' }}></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-foreground">{notif.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{notif.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-2 font-semibold">
                      {formatDateTime(notif.created_at)}
                    </p>
                  </div>
                  {!notif.read && (
                    <div className="absolute top-2 right-2 px-2 py-1 bg-primary text-white text-[10px] font-bold rounded-full">
                      NUEVA
                    </div>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationPanel;